import { createSlice } from '@reduxjs/toolkit';
import {
	exportTransactionsLedgerCsv,
	fetchDealershipOptions,
	fetchPaymentMethodOptions,
	fetchTransactionsLedgerRecords,
	fetchUserOptions,
	updateFiltersAndFetch,
	updatePageAndFetch,
	updateSortingAndFetch,
} from './thunks';
import { DEFAULT_SORTING, FILTERS_STORAGE_KEY, initialState, SORTING_KEYS } from './constants';

export const transactionsLedgerSlice = createSlice({
	name: 'transactionsLedger',
	initialState,
	reducers: {
		setFilters: (state, { payload }) => {
			state.query = {
				...state.query,
				filters: {
					...state.query.filters,
					...payload,
				},
				page: 1,
			};
		},
		setSorting: (state, { payload }) => {
			state.query.sorting =
				payload?.order_by ?
					{
						order_by: payload.order_by,
						direction: payload.direction || 'asc',
					}
				:	DEFAULT_SORTING;
			state.query.page = 1;
		},
		setPage: (state, { payload }) => {
			state.query = {
				...state.query,
				page: payload || 1,
			};
		},
		clearErrors: (state) => {
			state.errors = initialState.errors;
		},
		clearNonDataErrors: (state) => {
			state.errors = {
				...state.errors,
				exportErrorMessage: '',
				dealershipsErrorMessage: '',
				usersErrorMessage: '',
				paymentMethodsErrorMessage: '',
			};
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchTransactionsLedgerRecords.pending, (state) => {
				state.loading = true;
				state.success = undefined;
				state.errors.dataErrorMessage = '';
			})
			.addCase(fetchTransactionsLedgerRecords.fulfilled, (state, { payload }) => {
				const { records = [], metadata = {} } = payload || {};

				state.records = records;
				state.metadata = {
					page: Number(metadata?.page),
					per_page: Number(metadata?.per_page),
					page_count: Number(metadata?.page_count),
					total_count: Number(metadata?.total_count),
				};
				state.loading = false;
				state.success = true;
			})
			.addCase(fetchTransactionsLedgerRecords.rejected, (state, { payload }) => {
				state.loading = false;
				state.success = false;
				state.errors.dataErrorMessage = payload || 'Failed to load transactions ledger records.';
			})
			.addCase(fetchDealershipOptions.pending, (state) => {
				state.dealershipOptions.loading = true;
				state.errors.dealershipsErrorMessage = '';
			})
			.addCase(fetchDealershipOptions.fulfilled, (state, { payload }) => {
				state.dealershipOptions.loading = false;
				state.dealershipOptions.data = payload || [];
			})
			.addCase(fetchDealershipOptions.rejected, (state, { payload }) => {
				state.dealershipOptions.loading = false;
				state.errors.dealershipsErrorMessage = payload || 'Failed to load dealerships.';
			})
			.addCase(fetchUserOptions.pending, (state) => {
				state.userOptions.loading = true;
				state.errors.usersErrorMessage = '';
			})
			.addCase(fetchUserOptions.fulfilled, (state, { payload }) => {
				state.userOptions.loading = false;
				state.userOptions.data = payload || [];
			})
			.addCase(fetchUserOptions.rejected, (state, { payload }) => {
				state.userOptions.loading = false;
				state.errors.usersErrorMessage = payload || 'Failed to load users.';
			})
			.addCase(fetchPaymentMethodOptions.pending, (state) => {
				state.paymentMethodOptions.loading = true;
				state.errors.paymentMethodsErrorMessage = '';
			})
			.addCase(fetchPaymentMethodOptions.fulfilled, (state, { payload }) => {
				state.paymentMethodOptions.loading = false;
				state.paymentMethodOptions.data = payload || [];
			})
			.addCase(fetchPaymentMethodOptions.rejected, (state, { payload }) => {
				state.paymentMethodOptions.loading = false;
				state.errors.paymentMethodsErrorMessage = payload || 'Failed to load payment methods.';
			})
			.addCase(exportTransactionsLedgerCsv.pending, (state) => {
				state.exportingCsv = true;
				state.errors.exportErrorMessage = '';
			})
			.addCase(exportTransactionsLedgerCsv.fulfilled, (state) => {
				state.exportingCsv = false;
			})
			.addCase(exportTransactionsLedgerCsv.rejected, (state, { payload }) => {
				state.exportingCsv = false;
				state.errors.exportErrorMessage = payload || 'Failed to export CSV.';
			});
	},
});

export const { setFilters, setSorting, setPage, clearErrors, clearNonDataErrors } = transactionsLedgerSlice.actions;

export default transactionsLedgerSlice.reducer;

export const selectTransactionsLedgerSyncConfig = () => ({
	storageKey: FILTERS_STORAGE_KEY,
	sortingKeys: SORTING_KEYS,
	filterActionCreator: updateFiltersAndFetch,
	sortingActionCreator: updateSortingAndFetch,
	pageActionCreator: updatePageAndFetch,
});
